import React, { useEffect } from 'react';
import { Field, useFormikContext } from 'formik';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Stack,
    SelectChangeEvent,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { ComboBox, PhoneInputField, UCNField, YesNoSelector } from '@fnol/library/src/components';
import {
    GenericObject,
    getApplicationId,
    getIsSalesForce,
    hasValueAndIsSalesForce,
    maxCharacterLimit,
} from '@fnol/library/src/lib';
import {
    bodyHarm,
    constValues,
    legalPersonType,
    personType,
    requestType,
    translations,
    nameMaxLength,
    surnameMaxLength,
    question50MaxLength,
    questionMaxLength,
} from '../../lib/consts';
import { preparePersonRelationsList } from '../../lib/helpers';
import { LegalLeasingCleanup } from '../LegalLeasingEntity/LegalLeasingEntity';

type IndividualProps = {
    individualType: personType;
    id?: number;
};

const Individual = ({ individualType, id }: IndividualProps) => {
    const { values, errors, touched, handleChange, setFieldValue } = useFormikContext<any>();
    const name = `${personType[individualType]}IndividualName`;
    const lastname = `${personType[individualType]}IndividualLastName`;
    const ucn = `${personType[individualType]}IndividualUCN`;
    const country = `${personType[individualType]}IndividualCountry`;
    const countryText = `${personType[individualType]}IndividualMyCountry`;
    const city = `${personType[individualType]}IndividualCity`;
    const cityInSerbia = `${personType[individualType]}IndividualCityInSerbia`;
    const street = `${personType[individualType]}IndividualStreet`;
    const email = `${personType[individualType]}IndividualEmail`;
    const phone = `${personType[individualType]}IndividualPhone`;
    const legalPersonTypePropertyName = `${personType[individualType]}Type`;
    const submitterRelationId = `${personType[individualType]}IndividualSubmitterRelationId`; // not exists for submitter
    const shouldShowBodyHarmForm = `${personType[individualType]}IndividualShouldShowBodyHarmForm`;
    const reportingImmaterialDamage = `${personType[individualType]}IndividualReportingImmaterialDamage`;
    const personRoleId = `${personType[individualType]}IndividualPersonRoleId`;
    const description = `${personType[individualType]}IndividualImmaterialDamageDescription`;
    const duplicatedUcn = `${personType[individualType]}DuplicatedUcn`;

    const insertTypes: number[] = [
        personType.submitter,
        personType.owner,
        personType.user,
        personType.driver,
        personType.injured,
        personType.ownerBuilding,
    ];
    const injuredIds = [1, 2, 3, 4, 5]; // list of multiple persons

    const checkIfDriverIsSelected = (): boolean => {
        let result = false;

        insertTypes.map((insertType) => {
            if (insertType === personType.injured) {
                injuredIds.map((injuredId) => {
                    if (
                        injuredId !== id &&
                        values[`${personType[insertType]}${injuredId}IndividualPersonRoleId`] === constValues.driver
                    ) {
                        // skip id that is edited for injured persons (multiple persons can be entered)
                        result = true;
                    }
                });
            } else if (
                insertType !== individualType &&
                insertType !== personType.driver && // driver always have role constValues.driver
                values[`${personType[insertType]}IndividualPersonRoleId`] === constValues.driver
            ) {
                // skip current type of person (only one person can be entered)
                result = true;
            }
        });
        return result;
    };

    let personRoleOptions = values[`${personType[individualType]}PersonRoles`];
    // check if driver is selected for other person
    if (checkIfDriverIsSelected()) {
        personRoleOptions = personRoleOptions?.filter((opt: any) => opt.id !== constValues.driver);
    }

    const isPersonDriver = () => {
        if (
            values.policyUCN === values[ucn] &&
            values[personRoleId] === constValues.driver &&
            values.requestType !== requestType.MaterialDamage
        ) {
            return true;
        } else {
            return false;
        }
    };

    useEffect(() => {
        if (!!isPersonDriver()) {
            setFieldValue('personIsDriver', true);
        } else {
            setFieldValue('personIsDriver', false);
        }
    }, [isPersonDriver()]);

    const checkIfUCNExists = (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (values.requestType === requestType.Both || values.requestType === requestType.ImmaterialDamage) {
            const filteredClaims = values.reportedClaims?.filter(
                (claim: GenericObject) => !!claim.jmbg && claim.jmbg === values[ucn],
            );

            const insertedUcns: GenericObject = {};

            insertTypes.map((insertType) => {
                if (insertType === personType.injured) {
                    injuredIds.map((injuredId) => {
                        if (injuredId !== id) {
                            // skip id that is edited for injured persons (multiple persons can be entered)
                            insertedUcns[`${personType[insertType]}${injuredId}IndividualUCN`] =
                                values[`${personType[insertType]}${injuredId}IndividualUCN`];
                        }
                    });
                } else if (insertType !== individualType) {
                    // skip current type of person (only one person can be entered)
                    insertedUcns[`${personType[insertType]}IndividualUCN`] =
                        values[`${personType[insertType]}IndividualUCN`];
                }
            });

            let duplicatedUcnExists = false;

            for (const [key, value] of Object.entries(insertedUcns)) {
                if (e.target?.value && e.target?.value !== '' && value === e.target?.value) {
                    duplicatedUcnExists = true;
                }
            }

            setFieldValue(duplicatedUcn, duplicatedUcnExists);

            if (filteredClaims.length === 0) {
                setFieldValue(shouldShowBodyHarmForm, bodyHarm.CanBeSubmitted);
            } else {
                setFieldValue(shouldShowBodyHarmForm, bodyHarm.AlreadySubmitted);
            }
        } else {
            setFieldValue(shouldShowBodyHarmForm, bodyHarm.InitialValue);
        }
    };

    // selected country textual representation
    const selectedIndividualCountry = values.preparedCountries?.find(
        (item: any) => item.id === parseInt(values[country] as string),
    );
    values[countryText] = selectedIndividualCountry?.text;

    const handleChangeCountry = (event: SelectChangeEvent<HTMLSelectElement>) => {
        setFieldValue(country, event.target.value);
        setFieldValue(street, '');
        setFieldValue(city, '');
        setFieldValue(cityInSerbia, '');
    };

    const handleChangeSubmitterRelation = (event: SelectChangeEvent<HTMLSelectElement>) => {
        setFieldValue(submitterRelationId, event.target.value);

        if (personType[individualType] === 'owner') {
            IndividualCleanup(personType.user, setFieldValue);
            LegalLeasingCleanup(personType.user, setFieldValue);
            setFieldValue('howIsVehicleUsed', '');
            setFieldValue('submitterUserByVehicleRegistration', '');
            setFieldValue('userType', '');
        }
    };

    const handleChangePersonRole = (event: SelectChangeEvent<HTMLSelectElement>) => {
        setFieldValue(personRoleId, event.target.value);
        setFieldValue(values.isVehicleParked, '');
    };

    const handleChangeLegalPersonType = () => {
        if (values[legalPersonTypePropertyName] === legalPersonType.individual) {
            LegalLeasingCleanup(individualType, setFieldValue);
        }
    };

    const handleChangeUCN = () => {
        if (values[ucn] === '') {
            setFieldValue(description, '');
            if (individualType === personType.injured) {
                setFieldValue(reportingImmaterialDamage, 'true');
            } else {
                setFieldValue(reportingImmaterialDamage, null);
            }
            if (individualType === personType.driver) {
                setFieldValue(personRoleId, constValues.driver);
            } else {
                setFieldValue(personRoleId, '');
            }
        }
    };

    useEffect(() => {
        handleChangeLegalPersonType();
    }, [values[legalPersonTypePropertyName]]);

    useEffect(() => {
        handleChangeUCN();
    }, [values[ucn]]);

    useEffect(() => {
        if (values.requestType === requestType.MaterialDamage) {
            if (individualType !== personType.injured) setFieldValue(reportingImmaterialDamage, null); // for injured default value is set
            if (individualType !== personType.driver) setFieldValue(personRoleId, ''); // for driver default value is set
        }
        checkIfUCNExists(values[ucn]);
    }, [values.requestType]);

    useEffect(() => {
        if (values[reportingImmaterialDamage] === 'false') {
            if (individualType === personType.driver) {
                setFieldValue(personRoleId, constValues.driver);
            } else {
                setFieldValue(personRoleId, '');
            }
            setFieldValue(description, '');
        }
    }, [values[reportingImmaterialDamage]]);

    useEffect(() => {
        values.mobileLabelStar = true;
    }, []);

    useEffect(() => {
        if (individualType === personType.injured) {
            setFieldValue(reportingImmaterialDamage, 'true');
        } else {
            setFieldValue(reportingImmaterialDamage, values[reportingImmaterialDamage]);
        }
    }, []);

    return (
        <>
            <FormControl>
                <Field
                    name={name}
                    label={translations.personalInfo.NAME}
                    as={TextField}
                    inputProps={{
                        maxLength: nameMaxLength,
                    }}
                    required={true}
                    error={!!(touched[name] && errors[name])}
                />
                {touched[name] && errors[name] ? (
                    <FormHelperText error={true}>
                        <>{errors[name]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
            <FormControl>
                <Field
                    name={lastname}
                    label={translations.personalInfo.LAST_NAME}
                    as={TextField}
                    inputProps={{
                        maxLength: surnameMaxLength,
                    }}
                    required={true}
                    error={!!(touched[lastname] && errors[lastname])}
                />
                {touched[lastname] && errors[lastname] ? (
                    <FormHelperText error={true}>
                        <>{errors[lastname]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
            <FormControl error={!!(touched[country] && errors[country])}>
                <InputLabel id={country + '-label'}>{translations.COUNTRY}</InputLabel>
                <Select
                    id={country}
                    labelId={country + '-label'}
                    name={country}
                    value={values[country] || ''}
                    label={translations.COUNTRY}
                    onChange={handleChangeCountry}
                >
                    {values.countries?.map((option: any) => {
                        return (
                            <MenuItem key={option.id} value={option.id}>
                                {option.text}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>
            {values[country] !== constValues.countrySerbiaId.toString(10) &&
            values[country] !== constValues.countrySerbiaId ? (
                <FormControl>
                    <Field
                        name={city}
                        label={translations.CITY}
                        as={TextField}
                        inputProps={{
                            maxLength: question50MaxLength,
                        }}
                        error={!!(touched[city] && errors[city])}
                    />
                    {touched[city] && errors[city] ? (
                        <FormHelperText error={true}>
                            <>{errors[city]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            ) : (
                <FormControl>
                    <Field
                        name={cityInSerbia}
                        label={translations.CITY}
                        options={
                            values.cities || [
                                {
                                    id: '',
                                    value: '',
                                    text: '',
                                    code: '',
                                },
                            ]
                        }
                        onChange={handleChange}
                        defaultValue={values[cityInSerbia]}
                        as={ComboBox}
                        error={!!(touched[cityInSerbia] && errors[cityInSerbia])}
                    />
                    {touched[cityInSerbia] && errors[cityInSerbia] ? (
                        <FormHelperText error={true}>
                            <>{errors[cityInSerbia]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}
            <FormControl>
                <Field
                    name={street}
                    label={translations.STREET}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[street] && errors[street])}
                />
                {touched[street] && errors[street] ? (
                    <FormHelperText error={true}>
                        <>{errors[street]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
            <FormControl>
                <Field
                    name={ucn}
                    label={translations.personalInfo.UCN}
                    as={UCNField}
                    onBlur={checkIfUCNExists}
                    required={true}
                    error={!!(touched[ucn] && errors[ucn])}
                />
                {touched[ucn] && errors[ucn] ? (
                    <FormHelperText error={true}>
                        <>{errors[ucn]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            {values[shouldShowBodyHarmForm] === bodyHarm.AlreadySubmitted && !values[duplicatedUcn] ? (
                <Stack direction="row" alignItems="top" spacing={2} marginTop={2} marginBottom={3}>
                    <Info color="primary" />
                    <Typography variant="body2" paddingTop={0.5}>
                        {translations.validationMessages.CLAIM_ALREADY_EXISTS_FOR_UCN}
                    </Typography>
                </Stack>
            ) : values[duplicatedUcn] ? (
                <Stack direction="row" alignItems="top" spacing={2} marginTop={2} marginBottom={3}>
                    <Info color="primary" />
                    <Typography variant="body2" paddingTop={0.5}>
                        {translations.validationMessages.ALREADY_HAS_IMMATERIAL}
                    </Typography>
                </Stack>
            ) : null}

            {getIsSalesForce(getApplicationId()) && individualType === personType.submitter && (
                <Stack direction="row" alignItems="top" spacing={2} marginTop={2} marginBottom={3}>
                    <Info color="primary" />
                    <Typography variant="body2" paddingTop={0.5}>
                        {translations.validationMessages.CONTACT_REQUIRED}
                    </Typography>
                </Stack>
            )}

            <FormControl>
                <PhoneInputField
                    name={phone}
                    specialLabel={
                        values.mobileLabelStar &&
                        !(individualType == personType.submitter && hasValueAndIsSalesForce(values[email]))
                            ? translations.personalInfo.MOBILE_STAR
                            : translations.personalInfo.MOBILE
                    }
                    country={'rs'}
                    required={individualType === personType.submitter && !hasValueAndIsSalesForce(values[email])}
                />
            </FormControl>
            <FormControl>
                <Field
                    name={email}
                    label={translations.personalInfo.EMAIL}
                    type="email"
                    as={TextField}
                    inputProps={{
                        maxLength: questionMaxLength,
                    }}
                    required={individualType === personType.submitter && !hasValueAndIsSalesForce(values[phone])}
                    error={!!(touched[email] && errors[email])}
                />
                {touched[email] && errors[email] ? (
                    <FormHelperText error={true}>
                        <>{errors[email]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
            {individualType !== personType.initialValue && individualType !== personType.submitter ? (
                <FormControl required={true} error={!!(touched[submitterRelationId] && errors[submitterRelationId])}>
                    <InputLabel id={submitterRelationId + '-label'}>
                        {translations.personalInfo.SUBMITTER_RELATION}
                    </InputLabel>
                    <Select
                        id={submitterRelationId}
                        labelId={submitterRelationId + '-label'}
                        name={submitterRelationId}
                        value={values[submitterRelationId] || ''}
                        label={translations.personalInfo.SUBMITTER_RELATION}
                        onChange={handleChangeSubmitterRelation}
                    >
                        {preparePersonRelationsList(
                            values.personRelations,
                            individualType,
                            values.submitterType,
                            legalPersonType.individual,
                        )?.map((option: any) => {
                            return (
                                <MenuItem key={option.id} value={option.value}>
                                    {option.text}
                                </MenuItem>
                            );
                        })}
                    </Select>
                    {touched[submitterRelationId] && errors[submitterRelationId] ? (
                        <FormHelperText error={true}>
                            <>{errors[submitterRelationId]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            ) : null}
            {values[shouldShowBodyHarmForm] === bodyHarm.CanBeSubmitted ? (
                <>
                    {individualType !== personType.injured ? (
                        <>
                            <Typography variant="subtitle1" marginTop={5}>
                                {translations.personalInfo.REPORTING_IMMATERIAL_DAMAGE_OR_BODILY_HARM + ' *'}
                            </Typography>
                            <YesNoSelector name={reportingImmaterialDamage} />
                        </>
                    ) : null}
                    {values[reportingImmaterialDamage] === 'true' && individualType !== personType.driver ? (
                        <FormControl required={true} error={!!(touched[personRoleId] && errors[personRoleId])}>
                            <InputLabel id={personRoleId + '-label'}>
                                {translations.personalInfo.PERSON_ROLE}
                            </InputLabel>
                            <Select
                                id={personRoleId}
                                labelId={personRoleId + '-label'}
                                name={personRoleId}
                                value={values[personRoleId] || ''}
                                label={translations.personalInfo.PERSON_ROLE}
                                onChange={handleChangePersonRole}
                            >
                                {personRoleOptions?.map((option: any) => {
                                    return (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.text}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                            {touched[personRoleId] && errors[personRoleId] ? (
                                <FormHelperText error={true}>
                                    <>{errors[personRoleId]}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    ) : null}

                    {values[reportingImmaterialDamage] === 'true' ? (
                        <FormControl>
                            <Field
                                name={description}
                                label={translations.IMMATERIAL_DAMAGE_DESCRIPTION}
                                multiline={true}
                                as={TextField}
                                maxRows={5}
                                minRows={5}
                                inputProps={{
                                    maxLength: maxCharacterLimit,
                                }}
                                value={values[description]}
                                helperText={`${translations.REMAINING_CHARACTERS} ${
                                    maxCharacterLimit - values[description]?.length
                                }`}
                            />
                        </FormControl>
                    ) : null}
                </>
            ) : null}

            {!!values.personIsDriver ? (
                <Stack direction="row" alignItems="top" spacing={2} marginTop={2} marginBottom={3}>
                    <Info color="primary" />
                    <Typography variant="body2" paddingTop={0.5}>
                        {translations.NO_INJURED_DRIVER_REQUEST}
                    </Typography>
                </Stack>
            ) : null}
        </>
    );
};

export const IndividualCleanup = (
    type: personType,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
    injuredId?: number,
) => {
    const idInjured = injuredId ? injuredId : '';
    setFieldValue(`${personType[type]}${idInjured}IndividualName`, '');
    setFieldValue(`${personType[type]}${idInjured}IndividualLastName`, '');
    setFieldValue(`${personType[type]}${idInjured}IndividualUCN`, '');
    setFieldValue(`${personType[type]}${idInjured}IndividualEmail`, '');
    setFieldValue(`${personType[type]}${idInjured}IndividualPhone`, constValues.dialCodeSerbia);
    setFieldValue(`${personType[type]}${idInjured}IndividualCountry`, constValues.countrySerbiaId);
    setFieldValue(`${personType[type]}${idInjured}IndividualMyCountry`, '');
    setFieldValue(`${personType[type]}${idInjured}IndividualStreet`, '');
    setFieldValue(`${personType[type]}${idInjured}IndividualCity`, '');
    setFieldValue(`${personType[type]}${idInjured}IndividualCityInSerbia`, '');
    setFieldValue(`${personType[type]}${idInjured}IndividualShouldShowBodyHarmForm`, bodyHarm.InitialValue);
    setFieldValue(`${personType[type]}${idInjured}IndividualImmaterialDamageDescription`, '');
    if (type !== personType.injured)
        setFieldValue(`${personType[type]}${idInjured}IndividualReportingImmaterialDamage`, '');
    else setFieldValue(`${personType[type]}${idInjured}IndividualReportingImmaterialDamage`, 'true');
    if (type !== personType.driver) setFieldValue(`${personType[type]}${idInjured}IndividualPersonRoleId`, '');
    else setFieldValue(`${personType[type]}${idInjured}IndividualPersonRoleId`, constValues.driver); // for driver there is default value for field
    if (type !== personType.initialValue && type !== personType.submitter)
        setFieldValue(`${personType[type]}${idInjured}IndividualSubmitterRelationId`, '');
};

export default Individual;
